import CryptoJS from 'crypto-js'

const API_KEY = 'ZXY6CYPCEWHMCNPD6C4N'
const API_SECRET = 'M2#$MMNaZrqJjvXfdW5QUCX5VjJ4RegF5VLnRe5V'
const BASE_URL = 'https://api.podcastindex.org/api/1.0'

const CATEGORY_CACHE_KEY = 'closedrecord:podcast-categories'

function getAuthHeaders(): Record<string, string> {
  const timestamp = Math.floor(Date.now() / 1000)
  const hash = CryptoJS.SHA1(API_KEY + API_SECRET + timestamp)
  const authToken = hash.toString(CryptoJS.enc.Hex)
  return {
    'X-Auth-Key': API_KEY,
    'X-Auth-Date': String(timestamp),
    'Authorization': authToken,
    'User-Agent': 'ClosedRecord/1.0',
  }
}

async function podcastIndexFetch<T>(endpoint: string): Promise<T> {
  const headers = getAuthHeaders()
  const response = await fetch(`${BASE_URL}${endpoint}`, { headers })

  if (!response.ok) {
    throw new Error(`PodcastIndex API error: ${response.status} ${response.statusText}`)
  }

  const data = await response.json()

  if (data.status === 'false' || data.status === false) {
    throw new Error(data.description || 'PodcastIndex API returned an error')
  }

  return data as T
}

export type PodcastIndexFeed = {
  id: number
  title: string
  url: string
  originalUrl: string | null
  link: string | null
  description: string | null
  author: string | null
  ownerName: string | null
  image: string | null
  artwork: string | null
  lastUpdateTime: number | null
  lastCrawlTime: number | null
  lastParseTime: number | null
  lastHttpStatus: number | null
  contentType: string | null
  itunesId: number | null
  generator: string | null
  language: string | null
  explicit: boolean | null
  episodeCount: number | null
  newestItemPubdate: number | null
  oldestItemPubdate: number | null
  popularityScore: number | null
  categories: Record<string, string> | null
  podcastGuid: string | null
  priority: number | null
  dead: number | null
  type: number | null
  medium: string | null
}

export type PodcastIndexEpisode = {
  id: number
  title: string
  link: string | null
  description: string | null
  guid: string | null
  datePublished: number | null
  datePublishedPretty: string | null
  dateCrawled: number | null
  enclosureUrl: string | null
  enclosureType: string | null
  enclosureLength: number | null
  duration: number | null
  explicit: boolean | null
  episode: number | null
  episodeType: string | null
  season: number | null
  image: string | null
  feedId: number | null
  feedImage: string | null
  feedTitle: string | null
  feedUrl: string | null
}

export type PodcastIndexCategory = {
  id: number
  name: string
}

type SearchResponse = {
  status: string | boolean
  feeds: PodcastIndexFeed[]
  count: number
  query: string
  description: string
}

type EpisodesResponse = {
  status: string | boolean
  items: PodcastIndexEpisode[]
  count: number
  query: string
  description: string
}

type FeedResponse = {
  status: string | boolean
  feed: PodcastIndexFeed
  description: string
}

type TrendingResponse = {
  status: string | boolean
  feeds: PodcastIndexFeed[]
  count: number
  description: string
  max: string
  since: number | null
}

type CategoriesResponse = {
  status: string | boolean
  feeds: PodcastIndexCategory[]
  count: number
  description: string
}

export async function searchPodcasts(query: string, max = 24): Promise<PodcastIndexFeed[]> {
  const q = encodeURIComponent(query.trim())
  const res = await podcastIndexFetch<SearchResponse>(`/search/byterm?q=${q}&max=${max}`)
  return res.feeds ?? []
}

export async function searchPodcastsByPerson(person: string, max = 24): Promise<PodcastIndexFeed[]> {
  const q = encodeURIComponent(person.trim())
  const res = await podcastIndexFetch<SearchResponse>(`/search/byperson?q=${q}&max=${max}`)
  return res.feeds ?? []
}

export async function getPodcastByFeedId(feedId: number): Promise<PodcastIndexFeed | null> {
  const res = await podcastIndexFetch<FeedResponse>(`/podcasts/byfeedid?id=${feedId}`)
  return res.feed ?? null
}

export async function getEpisodesByFeedId(feedId: number, max = 50): Promise<PodcastIndexEpisode[]> {
  const res = await podcastIndexFetch<EpisodesResponse>(`/episodes/byfeedid?id=${feedId}&max=${max}`)
  return res.items ?? []
}

export async function getPodcastEpisode(guidOrId: string | number): Promise<PodcastIndexEpisode | null> {
  const byGuid = typeof guidOrId === 'string' && !/^\d+$/.test(guidOrId)
  const endpoint = byGuid
    ? `/episodes/byguid?guid=${encodeURIComponent(guidOrId as string)}`
    : `/episodes/byid?id=${guidOrId}`
  try {
    const res = await podcastIndexFetch<EpisodesResponse>(endpoint)
    return res.items?.[0] ?? null
  } catch {
    return null
  }
}

export async function getTrendingPodcasts(max = 20): Promise<PodcastIndexFeed[]> {
  const res = await podcastIndexFetch<TrendingResponse>(`/podcasts/trending?max=${max}`)
  return res.feeds ?? []
}

export async function getPodcastsByCategory(category: string, max = 20): Promise<PodcastIndexFeed[]> {
  const catId = await resolveCategoryId(category)
  const res = await podcastIndexFetch<TrendingResponse>(`/podcasts/trending?max=${max}${catId ? `&cat=${catId}` : ''}`)
  return res.feeds ?? []
}

export async function getCategories(): Promise<PodcastIndexCategory[]> {
  const cached = safeLocalGet(CATEGORY_CACHE_KEY) as { feeds?: PodcastIndexCategory[] } | null
  if (cached && cached.feeds) {
    return cached.feeds
  }
  const res = await podcastIndexFetch<CategoriesResponse>('/categories/list')
  if (res.feeds) {
    safeLocalSet(CATEGORY_CACHE_KEY, { feeds: res.feeds, ts: Date.now() })
  }
  return res.feeds ?? []
}

async function resolveCategoryId(category: string): Promise<number | null> {
  const trimmed = category.trim().toLowerCase()
  if (!trimmed) return null
  if (/^\d+$/.test(trimmed)) return parseInt(trimmed, 10)
  const cats = await getCategories()
  const match = cats.find(c => c.name.toLowerCase() === trimmed)
  return match?.id ?? null
}

export function normalizeFeed(feed: PodcastIndexFeed) {
  const cats = feed.categories ? Object.values(feed.categories) : []
  return {
    id: String(feed.id),
    title: feed.title || null,
    imageurl: feed.image || feed.artwork || null,
    description: feed.description || null,
    itunesauthor: feed.author || null,
    itunesownername: feed.ownerName || null,
    host: feed.generator || null,
    language: feed.language || null,
    episodecount: feed.episodeCount ?? null,
    category1: cats[0] || null,
    category2: cats[1] || null,
    category3: cats[2] || null,
    popularityscore: feed.popularityScore ?? null,
    newestitempubdate: feed.newestItemPubdate ? new Date(feed.newestItemPubdate * 1000).toISOString().split('T')[0] : null,
    createdon: feed.lastUpdateTime ? new Date(feed.lastUpdateTime * 1000).toISOString() : null,
    link: feed.link || null,
    newestenclosureurl: null as string | null,
    newestenclosureduration: null as string | null,
    feedId: feed.id,
  }
}

export function normalizeEpisode(ep: PodcastIndexEpisode) {
  return {
    id: ep.id,
    title: ep.title,
    description: ep.description || null,
    datePublished: ep.datePublished ? String(ep.datePublished) : null,
    date_published: ep.datePublished ? String(ep.datePublished) : null,
    duration: ep.duration ?? null,
    image: ep.image || null,
    enclosureUrl: ep.enclosureUrl || null,
    enclosure_url: ep.enclosureUrl || null,
    audioUrl: ep.enclosureUrl || null,
    audio_url: ep.enclosureUrl || null,
    link: ep.link || null,
    episodeNumber: ep.episode ?? null,
    episode_number: ep.episode ?? null,
    season: ep.season ?? null,
    feedImage: ep.feedImage || null,
    feed_image: ep.feedImage || null,
    guid: ep.guid || null,
  }
}

export type NormalizedFeed = ReturnType<typeof normalizeFeed>

export function safeLocalGet(key: string): Record<string, unknown> | null {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return null
    return JSON.parse(raw)
  } catch {
    return null
  }
}

export function safeLocalSet(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch {
    /* localStorage full or unavailable */
  }
}
